import React from "react";
import { Table, InputNumber, Input, Button, Row, Col, Select } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { CuerpoDocumentoItem } from "../types/facturaTypes";
import { parseNumber2Digits } from "../../../utils/ParseNumer/parseNumber2Digits";
import {
  formatCurrency,
  parseCurrency,
  calculateTotals,
} from "../utils/facturaUtils";
import { useCatalogos } from "../hooks/useCatalogos";

interface ProductTableProps {
  cuerpoDocumento: CuerpoDocumentoItem[];
  updateItem: (
    key: number,
    field: string,
    value: string | number | string[]
  ) => void;
  removeItem: (key: number) => void;
}

export const ProductTable: React.FC<ProductTableProps> = ({
  cuerpoDocumento,
  updateItem,
  removeItem,
}) => {
  const { catalogos } = useCatalogos();

  const columns = [
    {
      title: "Cantidad",
      dataIndex: "cantidad",
      width: 100,
      render: (value: number, record: CuerpoDocumentoItem) => (
        <InputNumber
          min={1}
          value={value}
          onChange={(val) => updateItem(record.key, "cantidad", val || 1)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Código",
      dataIndex: "codigo",
      width: 120,
      render: (value: string, record: CuerpoDocumentoItem) => (
        <Input
          value={value}
          onChange={(e) => updateItem(record.key, "codigo", e.target.value)}
          placeholder="Código"
        />
      ),
    },
    {
      title: "Unidad de Medida",
      dataIndex: "uniMedida",
      width: 140,
      render: (value: number, record: CuerpoDocumentoItem) => (
        <Select
          value={value}
          onChange={(val) => updateItem(record.key, "uniMedida", val)}
          style={{ width: "100%" }}
          options={catalogos.unidadesMedida}
          placeholder="Unidad"
        />
      ),
    },
    {
      title: "Descripción",
      dataIndex: "descripcion",
      width: 200,
      render: (value: string, record: CuerpoDocumentoItem) => (
        <Input
          value={value}
          onChange={(e) =>
            updateItem(record.key, "descripcion", e.target.value)
          }
          placeholder="Descripción del producto"
        />
      ),
    },
    {
      title: "Precio Unit.",
      dataIndex: "precioUni",
      width: 120,
      render: (value: number, record: CuerpoDocumentoItem) => (
        <InputNumber
          min={0}
          value={value}
          onChange={(val) => updateItem(record.key, "precioUni", val || 0)}
          formatter={formatCurrency}
          parser={parseCurrency}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Descuento",
      dataIndex: "montoDescu",
      width: 120,
      render: (value: number, record: CuerpoDocumentoItem) => (
        <InputNumber
          min={0}
          value={value}
          onChange={(val) => updateItem(record.key, "montoDescu", val || 0)}
          formatter={formatCurrency}
          parser={parseCurrency}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Código Tributo",
      dataIndex: "codTributo",
      width: 140,
      render: (value: string, record: CuerpoDocumentoItem) => (
        <Select
          value={value}
          onChange={(val) => updateItem(record.key, "codTributo", val)}
          style={{ width: "100%" }}
          options={catalogos.tributos}
          placeholder="Tributo"
          allowClear
        />
      ),
    },
    {
      title: "Venta Gravada",
      dataIndex: "ventaGravada",
      width: 120,
      render: (value: number) => <span>${parseNumber2Digits(value)}</span>,
    },
    {
      title: "IVA",
      dataIndex: "ivaItem",
      width: 100,
      render: (value: number) => <span>${parseNumber2Digits(value)}</span>,
    },
    {
      title: "Acciones",
      width: 80,
      render: (_: unknown, record: CuerpoDocumentoItem) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(record.key)}
          disabled={cuerpoDocumento.length === 1}
        />
      ),
    },
  ];

  const totals = calculateTotals(cuerpoDocumento);

  return (
    <Table
      columns={columns}
      dataSource={cuerpoDocumento}
      pagination={false}
      scroll={{ x: 1200 }}
      footer={() => (
        <div style={{ textAlign: "right" }}>
          <Row gutter={16} justify="end">
            <Col span={6}>
              <strong>Subtotal: ${parseNumber2Digits(totals.subtotal)}</strong>
            </Col>
            <Col span={6}>
              <strong>
                Descuento: ${parseNumber2Digits(totals.descuento)}
              </strong>
            </Col>
            <Col span={6}>
              <strong>IVA: ${parseNumber2Digits(totals.iva)}</strong>
            </Col>
            <Col span={6}>
              <strong>Total: ${parseNumber2Digits(totals.total)}</strong>
            </Col>
          </Row>
        </div>
      )}
    />
  );
};
